import { useEffect, useState } from "react";
import { ChartBar } from "@phosphor-icons/react";
import api from "../../lib/api";

const fmt$ = (n) => `$${Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;

function scoreColor(s) {
  if (s >= 80) return "text-emerald-700";
  if (s >= 60) return "text-amber-700";
  return "text-[#D0021B]";
}

function dotColor(s) {
  if (s >= 80) return "#047857";
  if (s >= 60) return "#B45309";
  return "#D0021B";
}

function paidRateClass(rate) {
  const r = rate || 0;
  if (r >= 10) return "text-emerald-700";
  if (r > 0) return "text-amber-700";
  return "text-zinc-400";
}

function QualityVolumeChart({ rows }) {
  const [hover, setHover] = useState(null);
  if (!rows.length) return null;

  const data = rows.map((r) => ({
    ref: r.ref,
    volume: r.lead_count,
    quality: r.avg_score,
    revenue: r.avg_revenue || 0,
  }));
  const W = 600, H = 240;
  const PAD_L = 56, PAD_R = 28, PAD_T = 18, PAD_B = 38;
  const innerW = W - PAD_L - PAD_R, innerH = H - PAD_T - PAD_B;

  const maxVol = Math.max(...data.map((d) => d.volume), 1);
  const xMax = Math.max(maxVol, 4);
  const maxRev = Math.max(...data.map((d) => d.revenue), 1);

  const xPos = (v) => PAD_L + (v / xMax) * innerW;
  const yPos = (q) => PAD_T + (1 - q / 100) * innerH;
  const rSize = (rev) => 6 + (rev / maxRev) * 14; // 6-20px radius

  const avgScore = data.reduce((a, b) => a + b.quality, 0) / data.length;
  const avgVolume = data.reduce((a, b) => a + b.volume, 0) / data.length;

  const xTicks = [0, Math.round(xMax * 0.25), Math.round(xMax * 0.5), Math.round(xMax * 0.75), xMax];
  const yTicks = [0, 25, 50, 75, 100];

  return (
    <div className="border-b border-zinc-200 px-2 py-3 bg-zinc-50" data-testid="lead-sources-chart">
      <div className="px-2 pb-2 flex items-baseline justify-between">
        <div className="font-mono text-[10px] tracking-[0.2em] text-zinc-500 uppercase">/ quality vs volume</div>
        <div className="font-mono text-[10px] text-zinc-400">avg {avgScore.toFixed(1)} score · {avgVolume.toFixed(1)} leads</div>
      </div>
      <div className="relative">
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" preserveAspectRatio="xMidYMid meet" role="img" aria-label="Quality vs volume scatter chart">
          {/* gridlines */}
          {yTicks.map((t) => (
            <g key={`y${t}`}>
              <line x1={PAD_L} y1={yPos(t)} x2={W - PAD_R} y2={yPos(t)} stroke="#e4e4e7" strokeDasharray="2 3" />
              <text x={PAD_L - 6} y={yPos(t) + 3} textAnchor="end" fontSize="9" fill="#71717a" fontFamily="JetBrains Mono, monospace">{t}</text>
            </g>
          ))}
          {xTicks.map((t) => (
            <g key={`x${t}`}>
              <line x1={xPos(t)} y1={PAD_T} x2={xPos(t)} y2={H - PAD_B} stroke="#e4e4e7" strokeDasharray="2 3" />
              <text x={xPos(t)} y={H - PAD_B + 14} textAnchor="middle" fontSize="9" fill="#71717a" fontFamily="JetBrains Mono, monospace">{t}</text>
            </g>
          ))}
          {/* axis labels */}
          <text x={W / 2} y={H - 4} textAnchor="middle" fontSize="9" fill="#71717a" fontFamily="JetBrains Mono, monospace" letterSpacing="0.15em">LEADS →</text>
          <text x={12} y={H / 2} textAnchor="middle" fontSize="9" fill="#71717a" fontFamily="JetBrains Mono, monospace" letterSpacing="0.15em" transform={`rotate(-90 12 ${H / 2})`}>AVG SCORE</text>
          {/* mean reference lines */}
          <line x1={PAD_L} y1={yPos(avgScore)} x2={W - PAD_R} y2={yPos(avgScore)} stroke="#a1a1aa" strokeDasharray="3 3" />
          <line x1={xPos(avgVolume)} y1={PAD_T} x2={xPos(avgVolume)} y2={H - PAD_B} stroke="#a1a1aa" strokeDasharray="3 3" />
          <text x={W - PAD_R - 4} y={yPos(avgScore) - 3} textAnchor="end" fontSize="8" fill="#a1a1aa">avg</text>
          {/* dots */}
          {data.map((d, i) => {
            const cx = xPos(d.volume);
            const cy = yPos(d.quality);
            const r = rSize(d.revenue);
            const c = dotColor(d.quality);
            const isHover = hover?.ref === d.ref;
            return (
              <g key={d.ref}>
                <circle
                  cx={cx} cy={cy} r={r}
                  fill={c} fillOpacity={isHover ? 0.85 : 0.55}
                  stroke={c} strokeWidth={isHover ? 2 : 1}
                  style={{ cursor: "pointer", transition: "fill-opacity 120ms" }}
                  onMouseEnter={() => setHover({ ...d, x: cx, y: cy })}
                  onMouseLeave={() => setHover(null)}
                  data-testid={`lead-chart-dot-${i}`}
                />
              </g>
            );
          })}
        </svg>
        {hover && (
          <div
            className="absolute pointer-events-none bg-white border border-zinc-950 px-2.5 py-1.5"
            style={{
              left: `${(hover.x / W) * 100}%`,
              top: `${(hover.y / H) * 100}%`,
              transform: `translate(${(hover.x / W) > 0.7 ? "calc(-100% + 16px)" : "-50%"}, calc(-100% - 12px))`,
              fontFamily: "IBM Plex Sans, sans-serif",
              whiteSpace: "nowrap",
            }}
            data-testid="lead-chart-tooltip"
          >
            <div className="font-mono text-[10px] tracking-[0.15em] text-zinc-500 uppercase">{hover.ref}</div>
            <div className="text-xs mt-0.5">
              <strong>{hover.volume}</strong> leads · score <strong style={{ color: dotColor(hover.quality) }}>{hover.quality.toFixed(1)}</strong>/100
            </div>
            <div className="text-[11px] text-zinc-500">{fmt$(hover.revenue)} avg revenue</div>
          </div>
        )}
      </div>
      <div className="px-2 pt-1 font-mono text-[9px] text-zinc-400">
        Bubble size ∝ avg revenue · top-right quadrant = high quality + high volume
      </div>
    </div>
  );
}

export default function LeadSourcesPanel() {
  const [rows, setRows] = useState([]);
  const [totalSources, setTotalSources] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/leads/sources?limit=10")
      .then((r) => { setRows(r.data?.items || []); setTotalSources(r.data?.total_sources || 0); })
      .catch(() => null)
      .finally(() => setLoading(false));
  }, []);

  return (
    <section className="border border-zinc-950 mt-8" data-testid="lead-sources-section">
      <div className="border-b border-zinc-950 bg-zinc-950 text-white px-4 py-2 font-mono text-[11px] tracking-[0.2em] uppercase flex items-center justify-between">
        <span><ChartBar size={12} className="inline mr-2" /> top.lead.sources</span>
        <span className="text-[10px]">{totalSources} unique source{totalSources === 1 ? "" : "s"}</span>
      </div>
      <div className="overflow-x-auto">
        {loading && <div className="p-8 text-center font-mono text-xs text-zinc-400 pulse-dot">LOADING…</div>}
        {!loading && rows.length === 0 && (
          <div className="p-8 text-center font-mono text-xs text-zinc-400">NO ATTRIBUTION DATA YET</div>
        )}
        {!loading && rows.length > 0 && (
          <>
            <QualityVolumeChart rows={rows} />
            <table className="w-full text-sm">
            <thead className="bg-zinc-50 border-b border-zinc-200 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">
              <tr>
                <th className="text-left px-4 py-2.5 w-10">#</th>
                <th className="text-left px-4 py-2.5">Source (ref)</th>
                <th className="text-right px-4 py-2.5">Leads</th>
                <th className="text-right px-4 py-2.5">Avg score</th>
                <th className="text-right px-4 py-2.5">Signups</th>
                <th className="text-right px-4 py-2.5">Paid</th>
                <th className="text-right px-4 py-2.5">Signup %</th>
                <th className="text-right px-4 py-2.5">Paid %</th>
                <th className="text-right px-4 py-2.5">Last seen</th>
              </tr>
            </thead>
            <tbody data-testid="lead-sources-tbody">
              {rows.map((r, i) => (
                <tr key={r.ref} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`lead-source-row-${i}`}>
                  <td className="px-4 py-2.5 font-mono text-xs text-zinc-400 tnum">{String(i + 1).padStart(2, "0")}</td>
                  <td className="px-4 py-2.5">
                    {r.ref === "direct" ? (
                      <span className="text-zinc-400 font-mono text-xs">direct</span>
                    ) : (
                      <span className="bg-amber-50 border border-amber-200 px-2 py-0.5 font-mono text-[11px]">{r.ref}</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-right font-display font-black text-lg tnum">{r.lead_count}</td>
                  <td className={`px-4 py-2.5 text-right font-bold tnum ${scoreColor(r.avg_score)}`}>
                    {r.avg_score}<span className="text-zinc-400 font-normal text-xs"> /100</span>
                  </td>
                  <td className="px-4 py-2.5 text-right tnum text-zinc-700" data-testid={`lead-source-row-${i}-signups`}>{r.signups ?? 0}</td>
                  <td className={`px-4 py-2.5 text-right tnum font-bold ${(r.paid || 0) > 0 ? "text-emerald-700" : "text-zinc-400"}`} data-testid={`lead-source-row-${i}-paid`}>
                    {r.paid ?? 0}
                  </td>
                  <td className="px-4 py-2.5 text-right tnum text-zinc-500">
                    {Number(r.signup_rate ?? 0).toFixed(1)}%
                  </td>
                  <td className={`px-4 py-2.5 text-right tnum font-bold ${paidRateClass(r.paid_rate)}`}>
                    {Number(r.paid_rate ?? 0).toFixed(1)}%
                  </td>
                  <td className="px-4 py-2.5 text-right text-xs text-zinc-500 tnum whitespace-nowrap">
                    {r.last_seen ? new Date(r.last_seen).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </>
        )}
      </div>
    </section>
  );
}
